import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { CaixaProvider } from '../context/CaixaContext';
import './AdminLayout.css';

const fullscreenSuportado = !!(
  document.documentElement.requestFullscreen ||
  document.documentElement.webkitRequestFullscreen
);

function estaEmFullscreen() {
  return !!(document.fullscreenElement || document.webkitFullscreenElement);
}

const ADMIN     = ['proprietario', 'administrador'];
const TODOS     = ['proprietario', 'administrador', 'funcionario', 'socio'];
const SEM_FUNC  = ['proprietario', 'administrador', 'socio'];

const menuItems = [
  { path: '/admin',                label: 'Dashboard',     icon: '📊', end: true, perfis: ADMIN     },
  { path: '/admin/pdv',            label: 'PDV',           icon: '🏪',            perfis: TODOS     },
  { path: '/admin/pedidos',        label: 'Pedidos',       icon: '🧾',            perfis: ADMIN     },
  { path: '/admin/cardapio',       label: 'Cardápio',      icon: '📋',            perfis: SEM_FUNC  },
  { path: '/admin/mesas',          label: 'Mesas / QR',    icon: '🪑',            perfis: TODOS     },
  { path: '/admin/comandas',       label: 'Comandas',      icon: '📝',            perfis: TODOS     },
  { path: '/admin/caixa',          label: 'Caixa',         icon: '💰',            perfis: TODOS     },
  { path: '/admin/producao',       label: 'Produção',      icon: '👨‍🍳',            perfis: TODOS     },
  { path: '/admin/estoque',        label: 'Estoque',       icon: '📦',            perfis: SEM_FUNC  },
  { path: '/admin/financeiro',     label: 'Financeiro',    icon: '💵',            perfis: ADMIN     },
  { path: '/admin/fiados',         label: 'Fiados',        icon: '📒',            perfis: SEM_FUNC  },
  { path: '/admin/clientes',       label: 'Clientes',      icon: '👤',            perfis: SEM_FUNC  },
  { path: '/admin/garcons',        label: 'Garçons',       icon: '🧑‍🍳',            perfis: ADMIN     },
  { path: '/admin/usuarios',       label: 'Usuários',      icon: '👥',            perfis: ADMIN     },
  { path: '/admin/ativos',         label: 'Ativo Fixo',    icon: '🏷️',            perfis: ADMIN     },
  { path: '/admin/relatorios',     label: 'Relatórios',    icon: '📈',            perfis: ADMIN     },
  { path: '/admin/configuracoes',  label: 'Configurações', icon: '⚙️',            perfis: ADMIN     },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(estaEmFullscreen());

  const menuFiltrado = menuItems.filter(item => item.perfis.includes(user?.perfil));

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    const onChange = () => setFullscreen(estaEmFullscreen());
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!estaEmFullscreen()) {
      const el = document.documentElement;
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el).catch(() => {});
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document).catch?.(() => {});
    }
  };

  return (
    <div className="admin-layout">
      {/* Overlay mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <span className="sidebar-logo">🥟</span>
          <div>
            <h2>Parada Predileta</h2>
            <small>Sistema de Gestão</small>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuFiltrado.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <span className="user-avatar">👤</span>
            <div>
              <strong>{user?.nome}</strong>
              <small>{user?.perfil}</small>
            </div>
          </div>
          <button className="btn btn-sm btn-outline" onClick={handleLogout}>Sair</button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        <header className="admin-header">
          <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <div className="header-right">
            {fullscreenSuportado && (
              <button
                className={`fullscreen-toggle ${fullscreen ? 'active' : ''}`}
                onClick={toggleFullscreen}
                title={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              >
                ⛶
              </button>
            )}
            <span className="user-greeting">Olá, {user?.nome}</span>
          </div>
        </header>
        <div className="admin-content">
          <CaixaProvider>
            <Outlet />
          </CaixaProvider>
        </div>
      </main>
    </div>
  );
}
